'use client';

import { useSearchParams } from 'next/navigation';
import type { PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Link } from '@packages/modules/src/Link/Link';
import type { BreadcrumbsFragmentFragment } from '@packages/gql/generated/shopping/graphql';

const TextWrapper = ({ children }: PropsWithChildren) => (
  <span className="text-left max-sm:whitespace-normal">{children}</span>
);

export type ExtendedBreadcrumbsBackButtonProps = Partial<
  Pick<NonNullable<BreadcrumbsFragmentFragment['breadCrumb']>[number], 'name' | 'uriPath'>
>;

/* 
  This component renders a breadcrumbs navigation

  mobile: there is only a button visible that links to the last item of the breadcrumbs data
  tablet: a back button and a collapsed breadcrumbs is visible
  desktop: a back button and a extended breadcrumbs is visible
*/

export const ExtendedBreadcrumbsBackButton = ({
  name,
  uriPath,
}: ExtendedBreadcrumbsBackButtonProps) => {
  const { formatMessage } = useIntl();

  const searchParams = useSearchParams();

  const [rememberedBackLink, setRememberedBackLink] = useState<string>(
    uriPath || `/s/${searchParams?.get('nav-q') || ''}`,
  );

  useEffect(() => {
    const pageData = window.sessionStorage.getItem('rememberedPageData');
    if (pageData) {
      const rememberedData = JSON.parse(pageData);
      setRememberedBackLink(rememberedData.path);
    }
  }, []);

  const buttonText = name ? (
    <TextWrapper>
      {formatMessage({
        defaultMessage: 'Zurück',
        id: 'extendedBreadcrumbs.button.back',
      })}
      <span className="md:hidden">
        {/* needed to have a whitespace in the text */}{' '}
        {formatMessage({
          defaultMessage: 'zu',
          id: 'extendedBreadcrumbs.button.backToExtension',
        })}{' '}
        {name}
      </span>
    </TextWrapper>
  ) : (
    <TextWrapper>
      {formatMessage({
        defaultMessage: 'Zurück zum Suchergebnis',
        id: 'extendedBreadcrumbs.button.backToSearchResult',
      })}
    </TextWrapper>
  );

  return (
    <Link href={rememberedBackLink} className="inline-flex">
      <Button variant="link" className="pr-1 whitespace-nowrap">
        <span className="icon-emp-arrow-medium-left" />
        {buttonText}
      </Button>
    </Link>
  );
};
